import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Star, MapPin, ExternalLink, ArrowRight, Loader2 } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'

interface MicrositeCard {
  subdomain: string
  subdomainType: string
  businessName: string
  category: string | null
  city: string | null
  rating: number | null
  reviewsCount: number
  logoUrl: string | null
  coverPhotoUrl: string | null
}

interface RootDomainLandingPageProps {
  ssrData?: {
    microsites: MicrositeCard[]
    totalCount: number
  }
}

const MICROSITE_BASE_DOMAIN = typeof window !== 'undefined'
  ? (window as any).__MICROSITE_BASE_DOMAIN__ || 'fly-social.site'
  : process.env.MICROSITE_BASE_DOMAIN || 'fly-social.site'

const FLY_LOGO_URL = '/lovable-uploads/2e7bf5aa-9c7b-41ea-b64a-48b87f3dc01d.png'

function MicrositeCardComponent({ site, index }: { site: MicrositeCard; index: number }) {
  const href = `https://${site.subdomain}.${MICROSITE_BASE_DOMAIN}`
  const initial = site.businessName.charAt(0).toUpperCase()
  const hue = site.subdomain.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="rl-card group"
      style={{ animationDelay: `${index * 0.06}s` }}
    >
      <div className="rl-card-cover">
        {site.coverPhotoUrl ? (
          <img src={site.coverPhotoUrl} alt={site.businessName} loading="lazy" className="rl-card-img" />
        ) : (
          <div className="rl-card-placeholder" style={{ background: `linear-gradient(135deg, hsl(${hue}, 35%, 25%), hsl(${(hue + 50) % 360}, 40%, 18%))` }}>
            <span className="rl-card-initial">{initial}</span>
          </div>
        )}

        <div className="rl-card-overlay">
          <span className="rl-card-visit-btn">
            <ExternalLink size={14} />
            Visit Site
          </span>
        </div>
      </div>

      <div className="rl-card-body">
        <div className="rl-card-header">
          {site.logoUrl ? (
            <div className="rl-card-logo">
              <img src={site.logoUrl} alt="" className="rl-card-logo-img" />
            </div>
          ) : (
            <div className="rl-card-logo rl-card-logo-fallback" style={{ background: `linear-gradient(135deg, hsl(${hue}, 40%, 30%), hsl(${(hue + 40) % 360}, 35%, 22%))` }}>
              <span>{initial}</span>
            </div>
          )}
          <h3 className="rl-card-title">{site.businessName}</h3>
        </div>

        {site.category && (
          <span className="rl-card-category">{site.category}</span>
        )}

        <div className="rl-card-meta">
          {site.rating != null && site.rating > 0 && (
            <span className="rl-card-rating">
              <Star size={12} fill="currentColor" />
              {site.rating.toFixed(1)}
              {site.reviewsCount > 0 && (
                <span className="rl-card-reviews">({site.reviewsCount})</span>
              )}
            </span>
          )}
          {site.city && (
            <span className="rl-card-city">
              <MapPin size={11} />
              {site.city}
            </span>
          )}
        </div>
      </div>
    </a>
  )
}

const PAGE_SIZE = 50

async function fetchMoreMicrosites(offset: number): Promise<MicrositeCard[]> {
  const { data: configs, error } = await supabase
    .from('subdomain_configs')
    .select('id, subdomain, subdomain_type, location_id, workspace_id')
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .range(offset, offset + PAGE_SIZE - 1)

  if (error || !configs || configs.length === 0) return []

  const locationIds = [...new Set(configs.filter((c: any) => c.location_id).map((c: any) => c.location_id))]
  const workspaceIds = [...new Set(configs.map((c: any) => c.workspace_id))]

  const [locationsResult, workspacesResult] = await Promise.all([
    locationIds.length > 0
      ? supabase.from('gmb_locations').select('id, name, primary_category, city, rating, reviews_count, media_items, metadata').in('id', locationIds)
      : { data: [], error: null },
    supabase.from('workspaces').select('id, name, brand_name, logo_url').in('id', workspaceIds)
  ])

  const locationMap = new Map((locationsResult.data || []).map((l: any) => [l.id, l]))
  const workspaceMap = new Map((workspacesResult.data || []).map((w: any) => [w.id, w]))

  return configs.map((config: any) => {
    const location = config.location_id ? locationMap.get(config.location_id) : null
    const workspace = workspaceMap.get(config.workspace_id)
    const locationName = location?.metadata?.locationName || location?.metadata?.title || location?.metadata?.displayName || (location?.name?.includes('/') ? location.name.split('/').pop() : location?.name) || null
    const logoUrl = location?.media_items?.logo?.googleUrl || location?.media_items?.logo?.sourceUrl || location?.metadata?.profilePhotoUrl || location?.metadata?.logoUrl || workspace?.logo_url || null

    return {
      subdomain: config.subdomain,
      subdomainType: config.subdomain_type,
      businessName: locationName || workspace?.brand_name || workspace?.name || config.subdomain,
      category: location?.primary_category || null,
      city: location?.city || null,
      rating: location?.rating || null,
      reviewsCount: location?.reviews_count || 0,
      logoUrl,
      coverPhotoUrl: location?.media_items?.coverPhoto?.googleUrl || location?.media_items?.coverPhoto?.sourceUrl || null,
    }
  }).filter((m: MicrositeCard) => m.businessName && !m.businessName.startsWith('accounts/'))
}

export default function RootDomainLandingPage({ ssrData }: RootDomainLandingPageProps) {
  const [microsites, setMicrosites] = useState<MicrositeCard[]>(ssrData?.microsites || [])
  const [loading, setLoading] = useState(false)
  const [allLoaded, setAllLoaded] = useState(false)
  const totalCount = ssrData?.totalCount || 0

  return (
    <>
      <Helmet>
        <title>Fly — Modern Business Websites</title>
        <meta name="description" content="Discover businesses powered by Fly. Create your free business website as a modern alternative to Google Business Sites." />
        <meta property="og:title" content="Fly — Modern Business Websites" />
        <meta property="og:description" content="Discover businesses powered by Fly. Free, AI-powered business websites." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <style dangerouslySetInnerHTML={{ __html: ROOT_LANDING_STYLES }} />

      <div className="rl-page">
        {/* Header */}
        <header className="rl-header">
          <div className="rl-container rl-header-inner">
            <a href="https://fly-social.com" className="rl-logo" target="_blank" rel="noopener noreferrer">
              <img src={FLY_LOGO_URL} alt="Fly" className="rl-logo-img" />
            </a>
          </div>
        </header>

        {/* Hero */}
        <section className="rl-hero">
          <div className="rl-hero-bg" />
          <div className="rl-container rl-hero-content">
            <span className="rl-hero-badge">
              The Modern Google Business Site Alternative
            </span>
            <h1 className="rl-hero-title">
              Beautiful Business Websites,
              <span className="rl-hero-gradient"> Built in Minutes</span>
            </h1>
            <p className="rl-hero-subtitle">
              {totalCount > 0
                ? 'Explore businesses already using Fly to power their online presence.'
                : 'AI-powered, SEO-optimized business websites — free and effortless.'}
            </p>
            <a href="https://fly-social.com" className="rl-hero-cta" target="_blank" rel="noopener noreferrer">
              Create Your Free Site
              <ArrowRight size={16} />
            </a>
          </div>
        </section>

        {/* Gallery */}
        {microsites.length > 0 && (
          <section className="rl-gallery">
            <div className="rl-container">
              <h2 className="rl-gallery-title">Latest Websites</h2>
              <div className="rl-grid">
                {microsites.map((site, i) => (
                  <MicrositeCardComponent key={site.subdomain} site={site} index={i} />
                ))}
              </div>
              {!allLoaded && microsites.length < totalCount && (
                <div className="rl-load-more">
                  <button
                    className="rl-load-more-btn"
                    disabled={loading}
                    onClick={async () => {
                      setLoading(true)
                      try {
                        const more = await fetchMoreMicrosites(microsites.length)
                        if (more.length === 0 || more.length < PAGE_SIZE) setAllLoaded(true)
                        setMicrosites(prev => [...prev, ...more])
                      } finally {
                        setLoading(false)
                      }
                    }}
                  >
                    {loading ? <><Loader2 size={16} className="rl-spinner" /> Loading...</> : `Show More (${totalCount - microsites.length} remaining)`}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="rl-footer">
          <div className="rl-container rl-footer-inner">
            <div className="rl-footer-brand">
              <img src={FLY_LOGO_URL} alt="Fly" className="rl-footer-logo" />
              <p className="rl-footer-tagline">Modern business websites, powered by Fly.</p>
            </div>
            <div className="rl-footer-links">
              <a href="https://fly-social.com/privacy-policy">Privacy</a>
              <a href="https://fly-social.com/terms-of-service">Terms</a>
              <a href="https://fly-social.com/contact">Contact</a>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}

const ROOT_LANDING_STYLES = `
  .rl-page {
    --rl-bg: #030712;
    --rl-surface: rgba(17, 24, 39, 0.7);
    --rl-text: #f9fafb;
    --rl-text-secondary: #9ca3af;
    --rl-text-muted: #6b7280;
    --rl-purple: #9b87f5;
    --rl-purple-bright: #8b5cf6;
    --rl-purple-dim: rgba(139, 92, 246, 0.12);
    --rl-purple-glow: rgba(139, 92, 246, 0.25);
    --rl-border: rgba(75, 85, 99, 0.35);
    --rl-card-border: rgba(75, 85, 99, 0.25);

    min-height: 100vh;
    background: var(--rl-bg);
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    color: var(--rl-text);
    -webkit-font-smoothing: antialiased;
  }
  .rl-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }
  @media (min-width: 640px) { .rl-container { padding: 0 28px; } }

  /* Header */
  .rl-header {
    position: sticky;
    top: 0;
    z-index: 50;
    background: rgba(3, 7, 18, 0.8);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid var(--rl-border);
  }
  .rl-header-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 60px;
  }
  .rl-logo { display: flex; align-items: center; text-decoration: none; }
  .rl-logo-img { height: 36px; width: auto; }
  .rl-header-cta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    background: var(--rl-purple);
    padding: 8px 18px;
    border-radius: 9999px;
    text-decoration: none;
    transition: all 0.2s;
    box-shadow: 0 0 16px var(--rl-purple-glow);
  }
  .rl-header-cta:hover {
    background: var(--rl-purple-bright);
    box-shadow: 0 0 24px rgba(139, 92, 246, 0.35);
    transform: translateY(-1px);
  }

  /* Hero */
  .rl-hero {
    position: relative;
    padding: 72px 0 56px;
    text-align: center;
    overflow: hidden;
  }
  .rl-hero-bg {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 70% 55% at 50% -5%, rgba(139, 92, 246, 0.15) 0%, transparent 70%),
      radial-gradient(ellipse 35% 40% at 25% 15%, rgba(168, 85, 247, 0.07) 0%, transparent 60%);
    pointer-events: none;
  }
  .rl-hero-content { position: relative; z-index: 1; }
  .rl-hero-badge {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--rl-purple);
    background: var(--rl-purple-dim);
    padding: 6px 16px;
    border-radius: 9999px;
    border: 1px solid rgba(139, 92, 246, 0.2);
    margin-bottom: 24px;
    animation: rl-fade-up 0.5s ease-out both;
  }
  .rl-hero-title {
    font-size: clamp(30px, 5vw, 52px);
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: -0.03em;
    color: var(--rl-text);
    margin: 0 auto 20px;
    max-width: 700px;
    animation: rl-fade-up 0.5s ease-out 0.06s both;
  }
  .rl-hero-gradient {
    background: linear-gradient(135deg, #a78bfa 0%, #c084fc 40%, #22d3ee 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .rl-hero-subtitle {
    font-size: 16px;
    line-height: 1.7;
    color: var(--rl-text-secondary);
    max-width: 500px;
    margin: 0 auto 32px;
    animation: rl-fade-up 0.5s ease-out 0.12s both;
  }
  .rl-hero-cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 13px 28px;
    background: var(--rl-purple);
    color: #fff;
    font-weight: 600;
    font-size: 14px;
    border-radius: 9999px;
    text-decoration: none;
    transition: all 0.2s;
    box-shadow: 0 0 24px var(--rl-purple-glow);
    animation: rl-fade-up 0.5s ease-out 0.18s both;
  }
  .rl-hero-cta:hover {
    background: var(--rl-purple-bright);
    box-shadow: 0 0 36px rgba(139, 92, 246, 0.4);
    transform: translateY(-2px);
  }

  /* Gallery */
  .rl-gallery {
    padding: 48px 0 72px;
    border-top: 1px solid var(--rl-border);
  }
  .rl-gallery-title {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin-bottom: 24px;
    color: var(--rl-text);
  }
  .rl-grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 16px;
  }
  @media (min-width: 520px) { .rl-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (min-width: 800px) { .rl-grid { grid-template-columns: repeat(3, 1fr); } }
  @media (min-width: 1100px) { .rl-grid { grid-template-columns: repeat(4, 1fr); } }

  /* Card */
  .rl-card {
    display: block;
    background: var(--rl-surface);
    border-radius: 16px;
    border: 1px solid var(--rl-card-border);
    overflow: hidden;
    text-decoration: none;
    color: var(--rl-text);
    transition: border-color 0.3s, transform 0.3s, box-shadow 0.3s;
    animation: rl-fade-up 0.4s ease-out both;
  }
  .rl-card:hover {
    border-color: rgba(139, 92, 246, 0.4);
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(139, 92, 246, 0.12), 0 0 0 1px rgba(139, 92, 246, 0.1);
  }
  .rl-card-cover { position: relative; height: 140px; overflow: hidden; }
  .rl-card-img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s ease; }
  .rl-card:hover .rl-card-img { transform: scale(1.08); }
  .rl-card-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
  .rl-card-initial { font-size: 44px; font-weight: 700; color: rgba(255,255,255,0.6); }

  .rl-card-header { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
  .rl-card-logo {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    overflow: hidden;
    background: #1f2937;
  }
  .rl-card-logo-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 700;
    color: rgba(255,255,255,0.7);
  }
  .rl-card-logo-img { width: 100%; height: 100%; object-fit: cover; }

  .rl-card-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.25s;
  }
  .rl-card:hover .rl-card-overlay { opacity: 1; }
  .rl-card-visit-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 16px;
    background: rgba(255,255,255,0.15);
    backdrop-filter: blur(8px);
    border-radius: 9999px;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid rgba(255,255,255,0.2);
  }

  .rl-card-body { padding: 14px; }
  .rl-card-title {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.35;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
  .rl-card-category {
    display: inline-block;
    font-size: 10px;
    font-weight: 500;
    color: var(--rl-purple);
    background: var(--rl-purple-dim);
    padding: 2px 8px;
    border-radius: 5px;
    margin-bottom: 8px;
  }
  .rl-card-meta { display: flex; align-items: center; gap: 10px; font-size: 11px; color: var(--rl-text-muted); }
  .rl-card-rating { display: inline-flex; align-items: center; gap: 3px; color: #facc15; font-weight: 600; }
  .rl-card-reviews { color: var(--rl-text-muted); font-weight: 400; margin-left: 1px; }
  .rl-card-city { display: inline-flex; align-items: center; gap: 3px; }

  /* Footer */
  .rl-footer {
    border-top: 1px solid var(--rl-border);
    padding: 28px 0;
    background: var(--rl-bg);
  }
  .rl-footer-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
  }
  .rl-footer-brand { display: flex; align-items: center; gap: 12px; }
  .rl-footer-logo { height: 28px; width: auto; opacity: 0.7; }
  .rl-footer-tagline { font-size: 12px; color: var(--rl-text-muted); margin: 0; }
  .rl-footer-links { display: flex; gap: 20px; }
  .rl-footer-links a { font-size: 12px; color: var(--rl-text-muted); text-decoration: none; transition: color 0.2s; }
  .rl-footer-links a:hover { color: var(--rl-purple); }

  /* Load More */
  .rl-load-more {
    display: flex;
    justify-content: center;
    margin-top: 32px;
  }
  .rl-load-more-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 28px;
    background: var(--rl-surface);
    color: var(--rl-text);
    font-weight: 600;
    font-size: 14px;
    border-radius: 9999px;
    border: 1px solid var(--rl-border);
    cursor: pointer;
    transition: all 0.2s;
  }
  .rl-load-more-btn:hover:not(:disabled) {
    border-color: rgba(139, 92, 246, 0.4);
    box-shadow: 0 0 16px var(--rl-purple-glow);
  }
  .rl-load-more-btn:disabled {
    opacity: 0.6;
    cursor: wait;
  }
  @keyframes rl-spin { to { transform: rotate(360deg); } }
  .rl-spinner { animation: rl-spin 1s linear infinite; }

  /* Animations */
  @keyframes rl-fade-up {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* Mobile */
  @media (max-width: 640px) {
    .rl-hero { padding: 56px 0 40px; }
    .rl-hero-title { font-size: 28px; }
    .rl-hero-subtitle { font-size: 15px; }
    .rl-footer-inner { flex-direction: column; text-align: center; }
    .rl-footer-brand { flex-direction: column; gap: 6px; }
  }
`
